import { Capacitor } from '@capacitor/core'

const isNative = Capacitor.isNativePlatform()

// Web SDK instance (lazy-initialized)
let webPurchasesInstance: any = null

/**
 * MODE plutôt que PROD : `vite build --mode staging` met lui aussi PROD à vrai,
 * or la boutique de test est exactement ce qu'on veut en staging. Seul le build
 * de production doit exiger une vraie caisse.
 */
const isProductionBuild = import.meta.env.MODE === 'production'

/** Clé de la boutique de test RevenueCat : elle simule les achats, sans encaisser. */
function isTestStoreKey(apiKey: string): boolean {
  return apiKey.startsWith('test_')
}

/**
 * L'init web en cours, pour que la caisse puisse être ATTENDUE.
 *
 * `initRevenueCat` part sans `await` depuis le listener d'auth et charge le
 * SDK en import dynamique. Entre l'arrivée dans l'app et la fin de cette
 * init, `webPurchasesInstance` vaut `null`. Sans cette promesse, un parent qui
 * touche le bouton dans cette fenêtre s'entend dire que l'abonnement se prend
 * sur mobile, ce qui est faux, et c'est le cas le plus probable puisqu'un
 * compte neuf arrive sur l'app et touche le bouton dans la foulée.
 */
let webInitPromise: Promise<void> | null = null

export async function initRevenueCat(userId: string) {
  if (isNative) {
    try {
      const platform = Capacitor.getPlatform()
      const apiKey = platform === 'ios'
        ? import.meta.env.VITE_REVENUECAT_APPSTORE_API_KEY
        : import.meta.env.VITE_REVENUECAT_PLAYSTORE_API_KEY
      if (!apiKey) {
        console.error('[Premium] No RevenueCat API key for platform:', platform)
        return
      }
      const { Purchases } = await import('@revenuecat/purchases-capacitor')
      await Purchases.configure({ apiKey })
      await Purchases.logIn({ appUserID: userId })
    } catch (error) {
      console.error('[Premium] RevenueCat native init error:', error)
    }
  } else {
    webInitPromise = initWebRevenueCat(userId)
    return webInitPromise
  }
}

async function initWebRevenueCat(userId: string): Promise<void> {
  try {
    const webApiKey = import.meta.env.VITE_REVENUECAT_WEB_API_KEY
    if (!webApiKey) {
      console.warn('[Premium] No VITE_REVENUECAT_WEB_API_KEY set, web paywall disabled')
      return
    }
    if (isTestStoreKey(webApiKey) && isProductionBuild) {
      // La boutique de test de RevenueCat imite une caisse sans rien
      // encaisser. En production elle vendait du vent : le parent voyait un
      // achat réussir et le webhook ouvrait un vrai Harmonia+ gratuitement
      // (le webhook refuse désormais store = TEST_STORE, donc désormais elle
      // ne donnerait même plus l'accès — un paywall qui ne fait rien).
      // Tant que la clé de production Web Billing n'est pas posée, mieux vaut
      // pas de caisse du tout qu'une caisse qui ment.
      console.error(
        '[Premium] VITE_REVENUECAT_WEB_API_KEY est une clé de boutique de test (test_…) ' +
        'dans un build de production : paywall web désactivé. Poser la clé Web Billing (rcb_…).',
      )
      return
    }
    const { Purchases } = await import('@revenuecat/purchases-js')
    webPurchasesInstance = Purchases.configure({ apiKey: webApiKey, appUserId: userId })
    console.info('[Premium] RevenueCat web SDK initialized for user', userId)
  } catch (error) {
    console.error('[Premium] RevenueCat web init error:', error)
  }
}

export type PurchaseOutcome =
  | { kind: 'purchased'; expiresAt: string | null; isLifetime: boolean }
  | { kind: 'restored'; expiresAt: string | null; isLifetime: boolean }
  /** Restauration aboutie, mais sans aucun droit à rendre. */
  | { kind: 'nothing_restored' }
  | { kind: 'cancelled' }

/** Ce que RevenueCat accorde à cet appareil, à l'instant. */
export interface ActiveEntitlement {
  /** Le parent a droit à Harmonia+, quelle qu'en soit la raison. */
  active: boolean
  /** La fin la plus lointaine connue, ou `null` quand il n'y en a pas. */
  expiresAt: string | null
  /** Un droit acquis pour de bon, qu'aucune expiration ne doit reprendre. */
  isLifetime: boolean
}

/**
 * L'offering demandé, ou `undefined` pour laisser RevenueCat servir le Current.
 *
 * Un identifiant introuvable (offering pas encore créé dans le dashboard,
 * faute de frappe, réseau coupé) retombe sur le Current au lieu de faire
 * échouer l'achat : mieux vaut le paywall par défaut qu'aucune caisse.
 */
async function resolveNativeOffering(offeringId: string | null | undefined) {
  if (!offeringId) return undefined
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor')
    const offerings = await Purchases.getOfferings()
    const offering = offerings.all?.[offeringId]
    if (!offering) {
      // Les identifiants réellement renvoyés, sans quoi un offering nommé
      // autrement dans le dashboard se traduit par un silence : le paywall
      // s'ouvre, sur le mauvais contrat, et rien ne le dit.
      console.error(
        `[Premium] Offering « ${offeringId} » absent de RevenueCat, repli sur le Current. ` +
        `Identifiants disponibles : ${Object.keys(offerings.all ?? {}).join(', ') || '(aucun)'}`,
      )
      return undefined
    }
    return offering
  } catch (error) {
    console.error('[Premium] getOfferings a échoué, repli sur le Current:', error)
    return undefined
  }
}

export async function purchasePremiumNative(
  householdId: string,
  supabaseUserId: string,
  offeringId?: string | null,
): Promise<PurchaseOutcome> {
  const { Purchases } = await import('@revenuecat/purchases-capacitor')
  await Purchases.setAttributes({
    household_id: householdId,
    supabase_user_id: supabaseUserId,
  })

  const { RevenueCatUI } = await import('@revenuecat/purchases-capacitor-ui')
  const { PAYWALL_RESULT } = await import('@revenuecat/purchases-capacitor')

  const offering = await resolveNativeOffering(offeringId)
  const { result } = await RevenueCatUI.presentPaywall(offering ? { offering } : undefined)

  switch (result) {
    case PAYWALL_RESULT.PURCHASED: {
      // On lit aussi customerInfo à l'achat : si le webhook RevenueCat tarde
      // (sandbox notamment), l'appelant peut réconcilier la DB directement.
      localStorage.removeItem('harmonia_premium_expired_notified')
      const { expiresAt, isLifetime } = await readActiveEntitlement()
      return { kind: 'purchased', expiresAt, isLifetime }
    }
    case PAYWALL_RESULT.RESTORED: {
      // RevenueCat does not fire a webhook on restore. Read the customer
      // info directly so the caller can reconcile the DB if needed.
      //
      // RESTORED dit que la restauration a ABOUTI, pas qu'elle a trouvé
      // quelque chose : un parent qui n'a jamais payé et touche « Restaurer »
      // reçoit ce même résultat. Le prendre pour un droit ouvrait Harmonia+ en
      // base, sans date de fin, donc pour toujours (HARMONIA-74). Seul
      // l'entitlement actif fait foi.
      const { active, expiresAt, isLifetime } = await readActiveEntitlement()
      if (!active) return { kind: 'nothing_restored' }
      localStorage.removeItem('harmonia_premium_expired_notified')
      return { kind: 'restored', expiresAt, isLifetime }
    }
    default:
      return { kind: 'cancelled' }
  }
}

/**
 * L'entitlement Harmonia+ tel que RevenueCat le voit à l'instant.
 *
 * `entitlements.active` ne contient que ce qui est ouvert : une seule clé
 * présente suffit donc à dire que le parent y a droit, sans relire `isActive`.
 *
 * `isLifetime` se lit sur l'ABSENCE de date de fin, confirmée par un
 * `willRenew` faux. Un abonnement en porte toujours une, y compris résilié
 * (elle dit alors jusqu'à quand il reste ouvert) ; un non-consommable n'en a
 * pas. C'est ce couple qui sépare « payé une fois pour toutes » de « on ne
 * connaît pas encore la date », que rien ne distinguait côté client et que la
 * base doit pourtant trancher, sans quoi une EXPIRATION reprend un achat à vie.
 */
export async function readActiveEntitlement(): Promise<ActiveEntitlement> {
  const none: ActiveEntitlement = { active: false, expiresAt: null, isLifetime: false }
  if (!isNative) return none
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor')
    const { customerInfo } = await Purchases.getCustomerInfo()
    const entitlements = customerInfo?.entitlements?.active ?? {}
    const keys = Object.keys(entitlements)
    if (keys.length === 0) return none

    let latest: number | null = null
    let lifetime = false
    for (const key of keys) {
      const ent: any = (entitlements as any)[key]
      const ms = ent?.expirationDateMillis ?? (ent?.expirationDate ? new Date(ent.expirationDate).getTime() : null)
      if (ms) {
        if (latest === null || ms > latest) latest = ms
      } else if (ent?.willRenew === false) {
        lifetime = true
      }
    }
    return {
      active: true,
      expiresAt: latest ? new Date(latest).toISOString() : null,
      isLifetime: lifetime,
    }
  } catch (err) {
    console.error('[Premium] readActiveEntitlement failed:', err)
    return none
  }
}

/** Même repli que côté natif : offering introuvable = paywall par défaut. */
async function resolveWebOffering(offeringId: string | null | undefined) {
  if (!offeringId || !webPurchasesInstance) return undefined
  try {
    const offerings = await webPurchasesInstance.getOfferings()
    const offering = offerings?.all?.[offeringId]
    if (!offering) {
      console.error(
        `[Premium] Offering « ${offeringId} » absent de RevenueCat, repli sur le Current. ` +
        `Identifiants disponibles : ${Object.keys(offerings?.all ?? {}).join(', ') || '(aucun)'}`,
      )
      return undefined
    }
    return offering
  } catch (error) {
    console.error('[Premium] getOfferings (web) a échoué, repli sur le Current:', error)
    return undefined
  }
}

export async function presentWebPaywall(htmlTarget: HTMLElement, offeringId?: string | null): Promise<boolean> {
  if (!webPurchasesInstance) {
    console.error('[Premium] Web purchases not initialized')
    return false
  }
  try {
    const offering = await resolveWebOffering(offeringId)
    const result = await webPurchasesInstance.presentPaywall({ htmlTarget, ...(offering ? { offering } : {}) })
    console.info('[Premium] Web paywall result:', result)
    localStorage.removeItem('harmonia_premium_expired_notified')
    return true
  } catch (error: any) {
    // errorCode 1 = UserCancelledError (back button / close)
    // errorCode 42 = TestStoreSimulatedPurchaseError (sandbox test purchase)
    if (error?.errorCode === 1) {
      console.info('[Premium] User cancelled paywall')
      return false
    }
    // 42 = TestStoreSimulatedPurchaseError. C'est un achat simulé, qui n'ouvre
    // rien : le traiter comme un succès n'a de sens qu'en dev et en staging,
    // où l'on veut justement dérouler le parcours sans payer. En production,
    // le faire reviendrait à féliciter un parent pour un achat qui n'existe pas.
    if (error?.errorCode === 42 && !isProductionBuild) {
      console.info('[Premium] Test store simulated purchase — treating as success')
      localStorage.removeItem('harmonia_premium_expired_notified')
      return true
    }
    console.error('[Premium] Web paywall error:', error)
    return false
  }
}

/**
 * La caisse web est-elle utilisable, une fois l'init laissée finir ?
 *
 * Asynchrone à dessein : répondre « non » parce que l'init n'est pas encore
 * revenue, c'est répondre faux. On attend la promesse en vol plutôt que de
 * lire un instantané.
 */
export async function ensureWebPaywallReady(): Promise<boolean> {
  if (webPurchasesInstance) return true
  if (webInitPromise) {
    await webInitPromise
    return webPurchasesInstance !== null
  }
  return false
}

export async function restorePurchases() {
  if (!isNative) return
  const { Purchases } = await import('@revenuecat/purchases-capacitor')
  return Purchases.restorePurchases()
}
